/**
 * @file InputComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de input
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2024-12-19
 * @modified 2026-01-22
 * @author Correcta
 * @author Adriana Hurtado
 * @author Cristina Bosa
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */

import { TYPE_BUTTON } from "@/constants/button";
import { Eye, EyeOff } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import GenericTooltip from "@/components/ui/GenericTooltip";

export interface InputComponentProps {
  readonly id: string;
  readonly label?: string;
  readonly placeholder: string;
  isRequired?: boolean;
  lang?: string;
  type:
    | "text"
    | "number"
    | "password"
    | "email"
    | "url"
    | "search"
    | "file"
    | "tel";
  name: string;
  value?: string | number | undefined;
  onChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (_e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (_e: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  error?: string;
  small?: boolean;
  readonly?: boolean;
  maxLength?: number;
  minLength?: number;
  accept?: string | string[];
  multiple?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  onIconClick?: () => void;
  noTranslation?: boolean;
  minNumber?: number;
  classNameSection?: string;
  step?: string;
  needAriaLabel?: boolean;
  tooltip?: string;
  ariaLabel?: string;
  autoComplete?: string;
}

const InputComponent = ({
  id,
  label,
  accept,
  placeholder,
  isRequired,
  type,
  name,
  value,
  onChange,
  onClick,
  error,
  readonly,
  maxLength = 100,
  minLength,
  multiple = false,
  className,
  noTranslation,
  minNumber,
  step,
  onKeyDown,
  needAriaLabel = true,
  icon,
  iconPosition = "right",
  onIconClick,
  tooltip,
  ariaLabel,
  autoComplete,
  lang,
}: InputComponentProps) => {
  const errors = useTranslations("Validation");
  const ariaLabelFn = useTranslations("AriaLabels");
  // Valor seguro para inputs controlados
  const safeValue = type !== "file" && value !== undefined ? value : undefined;
  const isA11yRequired = Boolean(isRequired || (label && label.includes("*")));
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <article className={"input-group"}>
      {label && (
        <label className={`label ${error ? "label--error" : ""}`} htmlFor={id}>
          {label} {tooltip && <GenericTooltip> {tooltip} </GenericTooltip>}
        </label>
      )}
      <section
        className={`input-group__section ${icon ? `input-group__section--with-icon input-group__section--icon-${iconPosition}` : ""}`}
      >
        {icon && iconPosition === "left" && (
          <span
            className={`input-group__icon input-group__icon--left ${onIconClick ? "input-group__icon--clickable" : ""}`}
            onClick={onIconClick}
            role={onIconClick ? "button" : undefined}
            tabIndex={onIconClick ? 0 : undefined}
            aria-label={
              onIconClick
                ? typeof icon === "string"
                  ? icon
                  : ariaLabelFn("inputField")
                : undefined
            }
            onKeyDown={
              onIconClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onIconClick();
                    }
                  }
                : undefined
            }
          >
            {icon}
          </span>
        )}
        <input
          id={id}
          name={name}
          className={`input ${error ? "input--error" : ""}  ${readonly ? "input--disabled" : ""} ${className ? className : ""} ${icon && iconPosition === "left" ? "input--with-icon-left" : ""} ${icon && iconPosition === "right" ? "input--with-icon-right" : ""}`}
          type={isPassword && showPassword ? "text" : type}
          value={safeValue}
          placeholder={placeholder}
          onChange={onChange}
          onClick={onClick}
          readOnly={readonly}
          onKeyDown={onKeyDown}
          maxLength={maxLength ? maxLength : undefined}
          minLength={minLength ? minLength : undefined}
          required={isRequired}
          aria-required={isA11yRequired}
          accept={Array.isArray(accept) ? accept.join(",") : accept}
          multiple={multiple}
          min={minNumber ? minNumber : undefined}
          step={step ? step : undefined}
          autoComplete={autoComplete}
          lang={lang}
          aria-label={
            ariaLabel ??
            (!label && needAriaLabel
              ? placeholder || ariaLabelFn("inputField")
              : undefined)
          }
        />
        {icon && iconPosition === "right" && !isPassword && (
          <span
            className={`input-group__icon input-group__icon--right ${onIconClick ? "input-group__icon--clickable" : ""}`}
            onClick={onIconClick}
            role={onIconClick ? "button" : undefined}
            tabIndex={onIconClick ? 0 : undefined}
            aria-label={
              onIconClick
                ? typeof icon === "string"
                  ? icon
                  : ariaLabelFn("inputField")
                : undefined
            }
            onKeyDown={
              onIconClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onIconClick();
                    }
                  }
                : undefined
            }
          >
            {icon}
          </span>
        )}
        {isPassword && (
          <button
            type={TYPE_BUTTON}
            onClick={handleTogglePasswordVisibility}
            className={`input-group__eye ${readonly ? "input-group__eye--disabled" : ""}`}
            aria-label={
              showPassword
                ? ariaLabelFn("hidePassword")
                : ariaLabelFn("showPassword")
            }
          >
            {showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        )}
      </section>
      {error && (
        <span className="input-group__error-message">
          {noTranslation ? error : errors(error)}
        </span>
      )}
    </article>
  );
};
export default InputComponent;
